import React, { useState, useEffect, useCallback, useRef, useMemo, useId } from "react";
import { DateTimePickerField } from "./DateTimePickerField";

interface CreateRoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateRoom: (options: {
    roomID: string;
    isTemporary: boolean;
    isPrivate: boolean;
    passCode?: string;
    title?: string;
    startDateTime?: string;
    endDateTime?: string;
    timezone?: string;
  }) => void;
  defaultRoomId: string;
  getTranslation?: (key: string, params?: Record<string, string>) => string;
  isCreating?: boolean;
  errorMessage?: string | null;
  onRoomIdChange?: () => void;
}

function toUtcOffset(tzName: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en", {
      timeZone: tzName,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    const offsetPart = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
    const match = offsetPart.match(/GMT([+-]\d+(?::\d+)?)?/);
    if (match) {
      const raw = match[1] ?? "+0";
      return raw.replace(/:00$/, "") || "+0";
    }
  } catch {
    // geçersiz timezone adı, olduğu gibi döndür
  }
  return tzName;
}

function toServiceDateTime(value: string): string {
  const normalizedValue = value.trim().replace(" ", "T");
  const [datePart, timePart = ""] = normalizedValue.split("T");
  if (!datePart || !timePart) return "";
  return `${datePart} ${timePart.slice(0, 5)}`;
}

export function CreateRoomModal({
  isOpen,
  onClose,
  onCreateRoom,
  defaultRoomId,
  getTranslation,
  isCreating,
  errorMessage,
  onRoomIdChange,
}: CreateRoomModalProps) {
  const [roomID, setRoomID] = useState(defaultRoomId);
  const [isTemporary, setIsTemporary] = useState(true);
  const [isPrivate, setIsPrivate] = useState(false);
  const [passCode, setPassCode] = useState("");
  const [title, setTitle] = useState("");
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [timezone, setTimezone] = useState("");
  const [dateTimeError, setDateTimeError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const timezoneDatalistId = useId();

  const timezoneOptions = useMemo(() => {
    const localTimezone =
      typeof Intl !== "undefined"
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : "";

    const suggestions = [
      localTimezone,
      "UTC",
      "Europe/Istanbul",
      "Europe/London",
      "Europe/Berlin",
      "America/New_York",
      "America/Los_Angeles",
      "Asia/Dubai",
      "Asia/Tokyo",
    ];

    return Array.from(new Set(suggestions.filter(Boolean)));
  }, []);

  const t = useCallback(
    (key: string, fallback: string) => {
      if (!getTranslation) return fallback;
      const translated = getTranslation(key);
      if (!translated || translated === key) return fallback;
      return translated;
    },
    [getTranslation]
  );

  const validateDateRange = useCallback(
    (startValue: string, endValue: string): string | null => {
      if (!startValue || !endValue) return null;

      const startTimestamp = Date.parse(startValue);
      const endTimestamp = Date.parse(endValue);

      if (
        Number.isNaN(startTimestamp) ||
        Number.isNaN(endTimestamp) ||
        endTimestamp > startTimestamp
      ) {
        return null;
      }

      return t(
        "memeetEndDateTimeAfterStart",
        "End date must be later than start date."
      );
    },
    [t]
  );

  const notifyFieldChange = useCallback(() => {
    onRoomIdChange?.();
  }, [onRoomIdChange]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setRoomID(defaultRoomId);
      setIsTemporary(true);
      setIsPrivate(false);
      setPassCode("");
      setTitle("");
      setStartDateTime("");
      setEndDateTime("");
      setTimezone("");
      setDateTimeError(null);
    }
  }, [isOpen, defaultRoomId]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const roomDateTimeError = validateDateRange(startDateTime, endDateTime);
      if (roomDateTimeError) {
        setDateTimeError(roomDateTimeError);
        return;
      }

      setDateTimeError(null);

      const payload: {
        roomID: string;
        isTemporary: boolean;
        isPrivate: boolean;
        passCode?: string;
        title?: string;
        startDateTime?: string;
        endDateTime?: string;
        timezone?: string;
      } = {
        roomID: roomID.trim() || defaultRoomId,
        isTemporary,
        isPrivate,
        passCode: isPrivate ? passCode : undefined,
      };

      const trimmedTitle = title.trim();
      const serviceStartDateTime = toServiceDateTime(startDateTime);
      const serviceEndDateTime = toServiceDateTime(endDateTime);
      const trimmedTimezone = timezone.trim();

      if (trimmedTitle) payload.title = trimmedTitle;
      if (serviceStartDateTime) payload.startDateTime = serviceStartDateTime;
      if (serviceEndDateTime) payload.endDateTime = serviceEndDateTime;
      if (trimmedTimezone) payload.timezone = toUtcOffset(trimmedTimezone);

      onCreateRoom(payload);
    },
    [
      roomID,
      isTemporary,
      isPrivate,
      passCode,
      title,
      startDateTime,
      endDateTime,
      timezone,
      defaultRoomId,
      onCreateRoom,
      validateDateRange,
    ]
  );

  if (!isOpen) return null;

  return (
    <div className="create-room-modal-overlay" onClick={handleOverlayClick}>
      <div className="create-room-modal" ref={modalRef}>
        <div className="create-room-modal-header">
          <h2>{t("memeetCreateRoomTitle", "Create a New Meeting")}</h2>
          <button
            type="button"
            className="create-room-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="create-room-field">
            <label htmlFor="roomID">{t("memeetRoomId", "Room ID")}</label>
            <input
              type="text"
              id="roomID"
              value={roomID}
              onChange={(e) => {
                setRoomID(e.target.value);
                notifyFieldChange();
              }}
              placeholder={defaultRoomId}
              aria-invalid={Boolean(errorMessage)}
            />
            {errorMessage ? (
              <span className="create-room-error" role="alert">
                {errorMessage}
              </span>
            ) : null}
          </div>

          <div className="create-room-field">
            <div className="field-label-row">
              <label htmlFor="title">{t("memeetRoomTitle", "Title")}</label>
              <span className="field-optional-chip">{t("memeetOptional", "Optional")}</span>
            </div>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                notifyFieldChange();
              }}
              placeholder={t("memeetRoomTitlePlaceholder", "e.g. Weekly Standup")}
            />
          </div>

          <div className="create-room-date-stack">
            <DateTimePickerField
              id="startDateTime"
              label={t("memeetStartDateTime", "Start Date & Time")}
              value={startDateTime}
              onChange={(nextValue) => {
                setStartDateTime(nextValue);
                setDateTimeError(validateDateRange(nextValue, endDateTime));
                notifyFieldChange();
              }}
              formatLabel={t("memeetDateTimeFormat", "YYYY-MM-DD HH:mm")}
              optionalLabel={t("memeetOptional", "Optional")}
              clearButtonLabel={t("memeetClearDateTime", "Clear date")}
              hint={t(
                "memeetStartDateTimeHint",
                "Example format: 2026-02-12 13:50"
              )}
            />

            <DateTimePickerField
              id="endDateTime"
              label={t("memeetEndDateTime", "End Date & Time")}
              value={endDateTime}
              min={startDateTime || undefined}
              onChange={(nextValue) => {
                setEndDateTime(nextValue);
                setDateTimeError(validateDateRange(startDateTime, nextValue));
                notifyFieldChange();
              }}
              formatLabel={t("memeetDateTimeFormat", "YYYY-MM-DD HH:mm")}
              optionalLabel={t("memeetOptional", "Optional")}
              clearButtonLabel={t("memeetClearDateTime", "Clear date")}
              invalid={Boolean(dateTimeError)}
              errorMessage={dateTimeError}
              hint={t(
                "memeetEndDateTimeHint",
                "Example format: 2026-02-12 13:50"
              )}
            />
          </div>

          <div className="create-room-field">
            <div className="field-label-row">
              <label htmlFor="timezone">{t("memeetTimezone", "Timezone")}</label>
              <span className="field-optional-chip">{t("memeetOptional", "Optional")}</span>
            </div>
            <input
              type="text"
              id="timezone"
              value={timezone}
              list={timezoneDatalistId}
              onChange={(e) => {
                setTimezone(e.target.value);
                notifyFieldChange();
              }}
              placeholder={t("memeetTimezonePlaceholder", "Europe/Istanbul")}
            />
            <datalist id={timezoneDatalistId}>
              {timezoneOptions.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
            <span className="field-hint">
              {t("memeetTimezoneHint", "If empty, server default will be used.")}
            </span>
          </div>

          <div className="create-room-toggle-field">
            <div className="toggle-label-wrapper">
              <span className="toggle-label">
                {t("memeetTemporaryRoom", "Temporary Room")}
              </span>
              <span className="toggle-description">
                {t(
                  "memeetTemporaryRoomDesc",
                  "Room will be deleted after the meeting ends"
                )}
              </span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={isTemporary}
                onChange={(e) => {
                  setIsTemporary(e.target.checked);
                  notifyFieldChange();
                }}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="create-room-toggle-field">
            <div className="toggle-label-wrapper">
              <span className="toggle-label">
                {t("memeetPrivateRoom", "Private Room")}
              </span>
              <span className="toggle-description">
                {t(
                  "memeetPrivateRoomDesc",
                  "Require a passcode to join the meeting"
                )}
              </span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={(e) => {
                  setIsPrivate(e.target.checked);
                  notifyFieldChange();
                }}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          {isPrivate && (
            <div className="create-room-field">
              <label htmlFor="passCode">{t("memeetPassCode", "Passcode")}</label>
              <input
                type="text"
                id="passCode"
                value={passCode}
                onChange={(e) => {
                  setPassCode(e.target.value);
                  notifyFieldChange();
                }}
                placeholder={t("memeetPassCodePlaceholder", "Enter passcode (optional)")}
              />
              <span className="field-hint">
                {t("memeetPassCodeHint", "Leave empty to auto-generate")}
              </span>
            </div>
          )}

          <div className="create-room-modal-actions">
            <button
              type="button"
              className="meet-button"
              onClick={onClose}
              disabled={isCreating}
            >
              {t("memeetCancel", "Cancel")}
            </button>
            <button
              type="submit"
              className="meet-button primary"
              disabled={isCreating}
            >
              {isCreating
                ? t("memeetCreating", "Creating...")
                : t("memeetCreateRoom", "Create Room")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}
